/**
 * QTC bridge transactions, linked across both chains (blackbeard/observer#44).
 *
 * A deposit is exact end to end: the bridge writes the depositor's Quantus
 * transaction hash into the NEAR mint, beside the Intents account it credits.
 * A withdrawal is exact on NEAR — `WITHDRAW_TO` names the Quantus address —
 * but custody's payout carries no NEAR hash, so the match is an inference by
 * the rule the API states, shown with the fee it implies.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { BridgeDeposits } from '../api/generated/BridgeDeposits'
import type { BridgeWithdrawals } from '../api/generated/BridgeWithdrawals'
import { RequestFailed, fetchBridgeDeposits, fetchBridgeWithdrawals } from '../api/rest'
import { height as fmtHeight, shortAddress, tokens, utc, when } from '../lib/format'
import { href } from '../lib/routes'

const REFRESH_MS = 120_000
const SHOWN = 25

function minutes(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 120) return `${seconds} s`
  return `${Math.round(seconds / 60)} min`
}

export function BridgeLinksPanel({ chain, symbol }: { chain: string; symbol: string }) {
  const [deposits, setDeposits] = useState<BridgeDeposits | null>(null)
  const [withdrawals, setWithdrawals] = useState<BridgeWithdrawals | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [all, setAll] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    const load = () =>
      Promise.all([
        fetchBridgeDeposits(chain, controller.signal),
        fetchBridgeWithdrawals(chain, controller.signal),
      ])
        .then(([d, w]) => {
          setDeposits(d)
          setWithdrawals(w)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain])

  const d = deposits?.decimals ?? withdrawals?.decimals ?? 12
  const amount = (raw: string) => `${tokens(raw, d)} ${symbol}`
  const account = (a: string) => (a.length > 24 ? shortAddress(a) : a)
  const depositRows = deposits ? (all ? deposits.deposits : deposits.deposits.slice(0, SHOWN)) : []
  const withdrawalRows = withdrawals
    ? all
      ? withdrawals.withdrawals
      : withdrawals.withdrawals.slice(0, SHOWN)
    : []

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Bridge transactions</h2>
        <span className="eyebrow">
          {deposits
            ? `${fmtHeight(deposits.linked)} of ${fmtHeight(deposits.deposits.length)} deposits linked to a NEAR mint`
            : 'Quantus ⇄ NEAR'}
        </span>
      </div>
      {error && <p className="empty">{error}</p>}
      {!error && !deposits && <p className="empty">Reading…</p>}

      {depositRows.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Deposits to NEAR, newest first</caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Sent on Quantus
                </th>
                <th scope="col" className="left">
                  From
                </th>
                <th scope="col">{symbol}</th>
                <th scope="col" className="left">
                  Minted on NEAR
                </th>
                <th scope="col" className="left">
                  To Intents account
                </th>
                <th scope="col">Took</th>
              </tr>
            </thead>
            <tbody>
              {depositRows.map((x) => (
                <tr key={`${x.height}-${x.quantus_tx ?? x.from}-${x.amount}`}>
                  <td className="left numeral" title={x.quantus_tx ?? 'hash not yet computed'}>
                    <Link
                      to={
                        x.quantus_tx
                          ? href({ chain, tx: x.quantus_tx })
                          : href({ chain, block: String(x.height) })
                      }
                    >
                      {x.at ? when(x.at) : `#${fmtHeight(x.height)}`}
                    </Link>
                  </td>
                  <td className="left" title={`${x.from} → ${x.deposit_address}`}>
                    <Link to={href({ chain, account: x.from })}>{account(x.from)}</Link>
                  </td>
                  <td className="numeral">{amount(x.amount)}</td>
                  <td className="left">
                    {x.mint ? (
                      <Link to={href({ chain, nearTx: x.mint.near_tx })} title={utc(x.mint.at)}>
                        {x.mint.near_tx.slice(0, 8)}…
                      </Link>
                    ) : (
                      <span className="panel-note" style={{ margin: 0 }}>
                        not linked
                      </span>
                    )}
                  </td>
                  <td className="left" title={x.mint?.account}>
                    {x.mint ? (
                      <Link to={href({ chain, intentsAccount: x.mint.account })}>
                        {account(x.mint.account)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="numeral">{minutes(x.seconds_to_mint)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {withdrawalRows.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Withdrawals to Quantus, newest first</caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Withdrawn on NEAR
                </th>
                <th scope="col" className="left">
                  From Intents account
                </th>
                <th scope="col">{symbol}</th>
                <th scope="col" className="left">
                  To Quantus address
                </th>
                <th scope="col" className="left">
                  Paid (inferred)
                </th>
                <th scope="col">Fee</th>
              </tr>
            </thead>
            <tbody>
              {withdrawalRows.map((w) => (
                <tr key={`${w.near_tx}-${w.near_height}-${w.amount}`}>
                  <td className="left numeral" title={utc(w.at)}>
                    <Link to={href({ chain, nearTx: w.near_tx })}>{when(w.at)}</Link>
                  </td>
                  <td className="left" title={w.account}>
                    <Link to={href({ chain, intentsAccount: w.account })}>
                      {account(w.account)}
                    </Link>
                  </td>
                  <td className="numeral">{amount(w.amount)}</td>
                  <td className="left" title={w.destination}>
                    <Link to={href({ chain, account: w.destination })}>
                      {account(w.destination)}
                    </Link>
                  </td>
                  <td className="left">
                    {w.payout ? (
                      <Link to={href({ chain, block: String(w.payout.height) })}>
                        #{fmtHeight(w.payout.height)}
                      </Link>
                    ) : (
                      <span className="panel-note" style={{ margin: 0 }}>
                        no payout matched
                      </span>
                    )}
                  </td>
                  <td className="numeral">{w.payout ? tokens(w.payout.fee, d) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {((deposits?.deposits.length ?? 0) > SHOWN ||
        (withdrawals?.withdrawals.length ?? 0) > SHOWN) &&
        !all && (
          <div className="pager">
            <button className="segmented panel-button" onClick={() => setAll(true)}>
              All →
            </button>
          </div>
        )}

      <p className="panel-note">
        A deposit is linked exactly: the bridge writes the depositor&apos;s Quantus transaction hash
        into the NEAR mint, beside the Intents account it credits, and a deposit address belongs to
        one account for good. That is what the bridge itself publishes on-chain; nothing here
        guesses who owns an address. A withdrawal is exact on NEAR, but custody&apos;s payout on
        Quantus names no NEAR transaction, so the payout shown is matched by rule —{' '}
        {withdrawals?.rule ?? 'see the API for the rule'}
      </p>
    </section>
  )
}
